import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { isNull, isUndefined, min } from 'lodash/fp';

import { useAppDispatch, useAppSelector } from '../../../../hooks/react-redux';
import { getPortfolioResponse, toggleDownloadModal, getGraphKeyColours, setKeyColourPair, PortfolioQuestion, getSurveySummary, PortfolioAnalysisPage, PortfolioAnalysisTemplateRequest, savePortfolioAnalyticsTemplateStarted, getPortfolioAnalyticsTemplates, toggleEmailModal } from '../store';
import styles from '../PortfolioAnalytics.module.scss';
import { useWindowResize } from '../../../../hooks/useWindowResize';
import { BarChartAnalytics } from '../../../shared/analytics/BarChart';
import { PieChartAnalyticsData, GraphKeyAndColour } from '../../../shared/analytics/PieChart';
import { PortfolioAnalyticsBarChart } from './PortfolioAnalyticsBarChart';
import { PortfolioAnalyticsPieChartWrapper } from './PortfolioAnalyticsPieChartWrapper';
import { IconButton } from '../../../shared/button/IconButton';
import { ARROW_WIDTH_OFFSET, Carousel } from '../../../shared/carousel/Carousel';
import { QuestionMark, Download, Mail } from '../../../shared/icons';
import { Scrollable } from '../../../shared/scrollable/Scrollable';
import { OverflowTooltip, CustomTooltip } from '../../../shared/tooltip';
import { PortfolioAnalyticsTable } from './PortfolioAnalyticsTable';
import { PortfolioAnalyticsMetaModal } from './PortfolioAnalyticsMetaModal';
import { PortfolioAnalyticsSaveTemplateModal } from './PortfolioAnalyticsSaveTemplateModal';
import { PortfolioAnalyticsResultsButtons } from './PortfolioAnalyticsResultsButtons';
import { useFetchStarted } from '../../../../hooks/useFetchStarted';
import { fetchAllBasicUsersStarted } from '../../users/store/actions';

const { french } = styles;

const NAV_BAR = 110;
const HEADER = 51;
const CAROUSEL_MARKERS = 20;
const CAROUSEL_TIMEOUT = 10000; // 10 seconds

interface PortfolioAnalyticsResultsProps {
    restartSurvey: () => void;
    backToTemplates: () => void;
    portfolioQuestion: PortfolioQuestion;
    changePageView: (view: PortfolioAnalysisPage) => void;
}

export const PortfolioAnalyticsResults: React.FC<PortfolioAnalyticsResultsProps> = ({ restartSurvey, portfolioQuestion, changePageView, backToTemplates }) => {
    useFetchStarted([fetchAllBasicUsersStarted()]);
    const dispatch = useAppDispatch();

    const [tileDimensions, setTileDimensions] = useState<undefined | { height: number; width: number; }>(undefined);
    const [screenWidth, screenHeight] = useWindowResize();
    const [selectedPieKey, setSelectedPieKey] = useState<string | undefined>(undefined);
    const [metaModalOpen, setMetaModalOpen] = useState(false);
    const [saveTemplateRequest, setSaveTemplateRequest] = useState<PortfolioAnalysisTemplateRequest | null>(null);

    const portfolioResponse = useAppSelector(getPortfolioResponse);
    const graphColours = useAppSelector(getGraphKeyColours);
    const summary = useAppSelector(getSurveySummary);
    const templates = useAppSelector(getPortfolioAnalyticsTemplates);

    const templateNameExists = useMemo(() => !isNull(saveTemplateRequest) && isUndefined(saveTemplateRequest.portfolioAnalysisTemplateId) && templates.map(({ templateName }) => templateName).includes(saveTemplateRequest.templateName), [saveTemplateRequest, templates]);
    const confirmDisabled = useMemo(() => saveTemplateRequest?.templateName === '' || templateNameExists, [saveTemplateRequest, templateNameExists]);
    const confirmDisabledTooltip = useMemo(() => {
        if (saveTemplateRequest?.templateName === '') {
            return ['Template requires a name'];
        }
        if (templateNameExists) {
            return ['Template name must be unique'];
        }
        return [];
    }, [saveTemplateRequest, templateNameExists]);

    const graphData = useMemo(() => !isNull(portfolioResponse) ? portfolioResponse.graphData : [], [portfolioResponse]);
    const chartHeight = useMemo(() => tileDimensions?.height ? tileDimensions.height - CAROUSEL_MARKERS : 100, [tileDimensions]);
    const chartWidth = useMemo(() => tileDimensions?.width ? tileDimensions.width - (2 * ARROW_WIDTH_OFFSET) : 100, [tileDimensions]);

    const openDownloadModal = useCallback(async () => {
        const svgWidth = tileDimensions!.width;
        const svgHeight = tileDimensions!.height;
        const originalPieSize = min([svgWidth, svgHeight])!;
        dispatch(toggleDownloadModal({ svgId: 'portfolio-analytics-pie-chart', height: originalPieSize, width: originalPieSize }));
    }, [dispatch, tileDimensions]);

    const openReportEmailModal = useCallback(async () => {
        const svgWidth = tileDimensions!.width;
        const svgHeight = tileDimensions!.height;
        const originalPieSize = min([svgWidth, svgHeight])!;
        dispatch(toggleEmailModal({ svgId: 'portfolio-analytics-pie-chart', height: originalPieSize, width: originalPieSize }));
    }, [dispatch, tileDimensions]);

    const saveTemplate = useCallback(() => {
        dispatch(savePortfolioAnalyticsTemplateStarted(saveTemplateRequest!));
        setSaveTemplateRequest(null);
    }, [saveTemplateRequest, dispatch]);

    const updateTemplateName = useCallback((value: string) => {
        const updatedTemplate = { ...saveTemplateRequest!, templateName: value };
        setSaveTemplateRequest(updatedTemplate);
    }, [saveTemplateRequest]);

    const pieHover = useCallback((data: PieChartAnalyticsData | null) => setSelectedPieKey(data?.key), []);
    const barHover = useCallback((data: BarChartAnalytics | null) => setSelectedPieKey(data?.name), []);
    const extractPieKeyAndColours = useCallback((colours: GraphKeyAndColour) => dispatch(setKeyColourPair(colours)), [dispatch]);

    const toggleMetaModal = useCallback(() => setMetaModalOpen(!metaModalOpen), [metaModalOpen]);

    const rotatingCharts = useMemo(() => {
        return [
            <PortfolioAnalyticsPieChartWrapper chartHeight={chartHeight} chartWidth={chartWidth} data={graphData} onHover={pieHover} extractKeyColours={extractPieKeyAndColours} key={1} />,
            <PortfolioAnalyticsBarChart chartHeight={chartHeight} chartWidth={chartWidth} data={graphData} onHover={barHover} graphColours={graphColours} key={2} />
        ];
    }, [pieHover, barHover, graphData, extractPieKeyAndColours, chartHeight, chartWidth, graphColours]);

    const carouselWidth = useMemo(() => !isUndefined(tileDimensions) ? tileDimensions.width - (2 * ARROW_WIDTH_OFFSET) : 0, [tileDimensions]);

    useEffect(() => {
        const questionHeight = NAV_BAR + HEADER + 25;
        if (screenWidth && screenHeight) {
            const width = (screenWidth - 40) * 0.3;
            const height = (screenHeight - questionHeight) / 2;
            setTileDimensions({ height, width });
        }
    }, [screenWidth, screenHeight]);

    return (
        <div className={styles.survey}>
            <div className={styles.surveyWrapper}>
                <div className={styles.resultsWrapper} style={{ height: screenHeight - (NAV_BAR + HEADER + 70) }}>
                    {!isNull(portfolioResponse) &&
                        <>
                            <div className={styles.surveySummaryWrapper}>
                                <div className={styles.surveySummaryLabel}>Question Asked:</div>
                                <OverflowTooltip className={styles.questionAsked} overlayText={summary.questionAsked} />
                                <CustomTooltip overlayText='Summary of information provided in the survey' placement='top'>
                                    <div className={styles.iconButtonWrapper}>
                                        <IconButton icon={QuestionMark} onClick={toggleMetaModal} color={french} />
                                    </div>
                                </CustomTooltip>
                                <CustomTooltip overlayText='Email Results' placement='top'>
                                    <div className={styles.iconButtonWrapper}>
                                        <IconButton icon={Mail} onClick={openReportEmailModal} color={french} />
                                    </div>
                                </CustomTooltip>
                                <CustomTooltip overlayText='Download Results' placement='top'>
                                    <div className={styles.iconButtonWrapper}>
                                        <IconButton icon={Download} onClick={openDownloadModal} color={french} />
                                    </div>
                                </CustomTooltip>
                            </div>
                            <div className={styles.resultHeaders}>
                                <div className={styles.summaryHeader}>Summary</div>
                                <div className={styles.breakdownHeader}>Breakdown</div>
                            </div>
                            <div className={styles.portfolioResults}>
                                <div className={styles.leftSideWrapper}>
                                    <div className={styles.summaryWrapper}>
                                        <Scrollable>
                                            <div className={styles.summary}>
                                                {portfolioResponse.summary}
                                            </div>
                                        </Scrollable>
                                    </div>
                                    <Carousel
                                        markers={[0, 1]}
                                        height={tileDimensions?.height || 0}
                                        width={carouselWidth}
                                        timeout={CAROUSEL_TIMEOUT}
                                    >
                                        {rotatingCharts}
                                    </Carousel>
                                </div>
                                <PortfolioAnalyticsTable
                                    selectedFieldLabel={summary.fieldLabel}
                                    askedPortfolioQuestion={portfolioQuestion}
                                    portfolioResponse={portfolioResponse}
                                    selectedPieKey={selectedPieKey}
                                    graphColours={graphColours}
                                />
                            </div>
                        </>
                    }
                </div>
            </div>
            <PortfolioAnalyticsResultsButtons
                restartSurvey={restartSurvey}
                portfolioQuestion={portfolioQuestion}
                changePageView={changePageView}
                setSaveTemplateRequest={setSaveTemplateRequest}
                backToTemplates={backToTemplates}
            />
            <PortfolioAnalyticsMetaModal modalOpen={metaModalOpen} closeModal={toggleMetaModal} summary={summary} />
            <PortfolioAnalyticsSaveTemplateModal
                modalOpen={!isNull(saveTemplateRequest)}
                closeModal={() => setSaveTemplateRequest(null)}
                confirm={saveTemplate}
                confirmDisabled={confirmDisabled}
                confirmDisabledTooltip={confirmDisabledTooltip}
                updateTemplateName={updateTemplateName}
                templateName={saveTemplateRequest?.templateName || ''}
            />
        </div>
    );
};
